import _ from 'lodash';
import { ref, computed, watch } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { getFamilyBasicInfoApi } from 'library-@/apis/core_basic_api/family';
import { useFamilySearchUrlParams } from './use-family-search-url-params';
import { create } from '../utils/deferred';
import type { IDeferred } from '../utils/deferred';

export interface IApiGetFamilyListRequest {
    index: number;
    type: string;
    q: string;
    efq?: string;
    family_type: string;
    family_key_words: boolean;
    space_id?: string;
    folder_id?: string;
    qid?: string;
    efqid?: string;
    layer_id?: string;
    field_id?: string;
    ai_field_id?: string;
    sort?: string;
    bio_uk?: string;
}

export interface IApiGetFamilyListResponse {
    total: number;
    basic_info_list: IApiGetFamilyListResponseItem[];
    family_key_words?: string[];
}

export interface IApiGetFamilyListResponseItem {
    index: number;
    patent_id: string;
    family_id: string;
}

export const useFamilySearchPage = createGlobalState(() => {
    const {
        familySearchIndex,
        familySearchType,
        q,
        efq,
        type,
        workspaceId,
        folderId,
        qid,
        efqid,
        layerId,
        fieldId,
        aiFieldId,
        sort,
        bioUk,
        updateUrlParams
    } = useFamilySearchUrlParams();

    const deferred = ref<IDeferred>(create<Partial<IApiGetFamilyListResponse>>());

    // 同族索引，从1开始
    const index = ref(familySearchIndex.value);
    const total = ref(0);
    const familyKeyWords = ref<string[]>([]);

    const hasPre = computed(() => index.value > 1);
    const hasNext = computed(() => index.value < total.value);
    const disabledPre = computed(() => !hasPre.value || isLoading.value);
    const disabledNext = computed(() => !hasNext.value || isLoading.value);

    const dataCache = ref<IApiGetFamilyListResponseItem[]>([]);
    const currentActiveItem = computed<Partial<IApiGetFamilyListResponseItem>>(() => {
        return _.find(dataCache.value, { index: index.value }) || {};
    });
    const isFirstLoading = ref(true);
    const isLoading = ref(false);
    const isLoadingRoot = ref(true);

    // 获取请求参数
    const getRequestParams = (pageIndex: number): IApiGetFamilyListRequest => {
        return {
            index: pageIndex,
            q: q.value,
            efq: efq.value,
            family_type: familySearchType.value,
            type: type.value,
            family_key_words: true,
            space_id: workspaceId.value,
            folder_id: folderId.value,
            // ws需要使用的参数
            qid: qid.value,
            efqid: efqid.value,
            layer_id: layerId.value,
            field_id: fieldId.value,
            ai_field_id: aiFieldId.value,
            sort: sort.value,
            bio_uk: bioUk.value,
        };
    };

    // 根据index批量获取数据
    const getPageList = async (pageIndex: number) => {
        if (isLoading.value) return;

        const matchItem = _.find(dataCache.value, { index: pageIndex });
        if (matchItem) return matchItem;

        isLoading.value = true;
        if (deferred.value.completed) deferred.value = create();
        try {
            const res: IApiGetFamilyListResponse = await getFamilyBasicInfoApi(getRequestParams(pageIndex));
            updateDataCache(res.basic_info_list);
            total.value = res.total;
            familyKeyWords.value = res.family_key_words || [];
            deferred.value.resolve(res);
        } catch (error) {
            deferred.value.resolve({});
        } finally {
            isLoading.value = false;
            isLoadingRoot.value = false;
        }
    };

    // 保证next有数据
    const preparePre = async () => {
        const preIndex = index.value - 1;
        const preItem = _.find(dataCache.value, { index: preIndex });
        if (!preItem && (preIndex >= 1)) {
            isLoading.value = true;
            try {
                const res: IApiGetFamilyListResponse = await getFamilyBasicInfoApi(getRequestParams(preIndex));
                updateDataCache(res.basic_info_list);
            } finally {
                isLoading.value = false;
            }
        }
    };

    // 保证pre有数据
    const prepareNext = async () => {
        const nextIndex = index.value + 1;
        const nextItem = _.find(dataCache.value, { index: nextIndex });
        if (!nextItem && (nextIndex <= total.value)) {
            isLoading.value = true;
            try {
                const res: IApiGetFamilyListResponse = await getFamilyBasicInfoApi(getRequestParams(nextIndex));
                updateDataCache(res.basic_info_list);
            } finally {
                isLoading.value = false;
            }
        }
    };

    // 更新缓存
    const updateDataCache = (newList: IApiGetFamilyListResponseItem[]) => {
        newList.forEach(item => {
            const matchItem = _.find(dataCache.value, { index: item.index });
            if (!matchItem) {
                dataCache.value.push(item);
            }
        });
    };

    // 上一个
    const togglePre = _.debounce(() => {
        if (!hasPre.value || isLoading.value) return;
        index.value -= 1;
    }, 500);

    // 下一个
    const toggleNext = _.debounce(() => {
        if (!hasNext.value || isLoading.value) return;
        index.value += 1;
    }, 500);

    // 更新数据
    watch(index, async (val) => {
        if (!val) return;
        await getPageList(val);
        await Promise.all([preparePre(), prepareNext()]);
    }, { immediate: true });

    // 同步url参数
    watch(currentActiveItem, (val) => {
        if (_.isEmpty(val)) return;
        // 第一次进入页面时，不更新url参数，切换同族时才进行触发
        if (isFirstLoading.value) {
            isFirstLoading.value = false;
        } else {
            updateUrlParams({ familySearchIndex: val.index, patentId: val.patent_id });
        }
        window.rvTrack(`View Family View -- ${folderId.value ? 'Workspace' : 'SRP'}`);
    }, { immediate: true });

    return {
        index,
        total,
        hasPre,
        hasNext,
        disabledPre,
        disabledNext,
        isLoading,
        isLoadingRoot,
        familyKeyWords,
        currentActiveItem,
        deferred,
        togglePre,
        toggleNext,
    };
});
